<script type="module" lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'

import PhotoSwipeLightbox from 'photoswipe/lightbox'
import Photoswipe from 'photoswipe'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import ShareAndroidIcon from '@lahaus-roomie/roomie/src/assets/icons/share-android.svg'

import 'photoswipe/style.css'

import type { Props } from './types'
import { useAppChatStore } from '@/stores/appChatStore'
import { getReferralBonus } from '@/utils/referralBonus'
import './_index.scss'

const appChatStore = useAppChatStore()

const props = withDefaults(defineProps<Props>(), {
  pictures: () => [],
  videos: () => [],
  webinars: () => [],
  tours: () => []
})

const emit = defineEmits(['opacityEvent'])

const { t, locale } = useI18n()
const { buildImageUrl } = useImsImageProvider()

const bonusWithCurrency = computed(() => getReferralBonus(locale.value, true))
const mediaTypes = ref(['pictures'])

const lightbox = ref()
const imagesZise = ref([{ width: 0, height: 0 }])

const stepUpTabs = () => {
  if (props.location.lat && props.location.lng) mediaTypes.value.push('location')
  if (props.videos.length > 0) mediaTypes.value.push('videos')
  if (props.tours.length > 0) mediaTypes.value.push('tours')
  if (props.webinars.length > 0) mediaTypes.value.push('webinars')
}

stepUpTabs()

const selectedTab = ref(props.currentTab)

const isBigByPosition = (index: number, size: number) => {
  return ((index + 1) % size) === 0
}

const videoSrc = (video: string): string => video.replace('watch?v=', 'embed/') + '?wmode=transparent'

const resolveSize = () => {
  const elements = document.getElementsByClassName('pictures-class')

  if (elements) {
    emit('opacityEvent', true)
    imagesZise.value = []

    for (const element of elements) {
      // @ts-ignore
      const width = element.offsetWidth * 3
      // @ts-ignore
      const height = element.offsetHeight * 3
      imagesZise.value.push({ width, height })
    }
  }
}

const handleReferAndEarnClick = () => {
  const endpointConfig = {
    screen: 'PDP',
    entryNameAlias: 'PDP_REFER_AND_EARN',
    startFlow: true,
    shouldCallBotForGreeting: true
  }
  appChatStore.openChatWindow(endpointConfig)
  props.handleCloseGallery()
}

const resolveImageDevice = (picture: string) => {
  return buildImageUrl(picture, {
    auto: 'format,compress,enhance',
    width: props.isMobile ? 600 : 1080
  })
}

const iframeHeight = (content: string[]) => content?.length > 1 ? 'h-350 md:h-[50svh]' : 'h-[70svh]'

const checkIfInView = () => {
  const container = document.querySelector('.gallery-elements-container')
  const elements = container!.children

  let closest = Infinity
  let closestElement = null

  for (const element of elements) {
    const rect = element.getBoundingClientRect()
    const containerRect = container!.getBoundingClientRect()

    const adjustedLeft = rect.left - containerRect.left
    const adjustedRight = rect.right - containerRect.left

    if (adjustedRight > 0 && adjustedLeft < container!.clientWidth) {
      const distance = Math.abs(container!.clientWidth / 2 - (adjustedLeft + rect.width / 2))

      if (distance < closest) {
        closest = distance
        closestElement = element
      }
    }
  }

  selectedTab.value = closestElement?.id

  const tabs = document.getElementById(`tab-${selectedTab.value}`)

  if (tabs?.scrollIntoView) {
    tabs.scrollIntoView({ inline: 'center' })
  }
}

const goToCurrentTab = (tab: string) => {
  const currentElement = document.getElementById(tab)
  if (currentElement?.scrollIntoView) {
    currentElement.scrollIntoView({ inline: 'center' })
    selectedTab.value = currentElement?.id
  }
}

const disableIframe = (event: Event) => {
  (event.target as HTMLElement).style.pointerEvents = 'none'
}

const enableIframe = (event: Event) => {
  (event.target as HTMLElement).style.pointerEvents = 'auto'
}

onMounted(() => {
  lightbox.value = new PhotoSwipeLightbox({
    gallerySelector: '#slider-gallery',
    childSelector: 'a',
    bgOpacity: 0,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: props.isMobile ? 0.5 : 1.4,
    maxZoomLevel: props.isMobile ? 1 : 3,
    pswpModule: Photoswipe,
    wheelToZoom: true,
    preload: [1, 2]
  })

  lightbox.value.init()

  lightbox.value.on('closingAnimationEnd', () => {
    emit('opacityEvent', false)
  })

  goToCurrentTab(selectedTab.value as string)
})

onUnmounted(() => {
  lightbox.value.destroy()
  lightbox.value = null
})

</script>

<script lang="ts">
export default {
  name: 'AppGallery'
}
</script>

<template>
  <div class="app-gallery">
    <div class="roomie-tabs mt-32 h-full">
      <div class="roomie-tabs__container">
        <ul class="roomie-tabs__header">
          <li
            v-for="(type) in mediaTypes"
            :id="`tab-${type}`"
            :key="type"
            :data-lh-id="`app-gallery-tab-${type}`"
            class="roomie-tabs__item capitalize"
            :class="{ 'is-selected': (type === selectedTab) }"
            @click="goToCurrentTab(type)">
            {{ t(type) }}
          </li>
        </ul>
      </div>

      <div
        class="gallery-elements-container w-full flex gap-24 mt-8 scrollbar-hidden"
        @scroll="checkIfInView">
        <div
          v-if="mediaTypes.includes('pictures')"
          id="pictures"
          class="snap-center w-full h-[70svh] flex-[0_0_auto]">
          <div
            v-show="selectedTab === 'pictures'"
            id="slider-gallery"
            class="h-full grid md:grid-cols-2 lg:grid-cols-3 auto-rows-[20rem] gap-16 overflow-y-auto scrollbar-hidden">
            <div
              v-for="(picture, index) in pictures"
              :key="picture"
              class="relative overflow-hidden"
              :class="{
                'md:col-span-1 md:row-span-1': !isBigByPosition(index, 3),
                'md:col-span-2 md:row-span-2': isBigByPosition(index, 3),
                'lg:col-span-1 lg:row-span-1': !isBigByPosition(index, 4),
                'lg:col-span-3 lg:row-span-2': isBigByPosition(index, 4)
              }">
              <a
                :id="`picture-${index}`"
                :data-lh-id="`app-gallery-${id}-picture-${index}`"
                :href="buildImageUrl(picture, { auto: 'compress,enhance,format' })"
                :data-pswp-width="imagesZise[index]?.width"
                :data-pswp-height="imagesZise[index]?.height"
                class="w-[calc(100%+60px)] h-full flex items-center absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                rel="noreferrer"
                @click="resolveSize">
                <img
                  :src="resolveImageDevice(picture)"
                  :alt="`project ${index}`"
                  class="pictures-class w-full object-contain pointer-events-none">
              </a>
            </div>
          </div>
        </div>

        <div
          v-if="mediaTypes.includes('location')"
          id="location"
          class="snap-center w-full h-[70svh] flex-[0_0_auto]">
          <AppMapDynamic
            v-if="selectedTab === 'location'"
            :id="location.mapId"
            :lat="location.lat"
            :lng="location.lng"
            styles="w-full h-[70svh]" />
        </div>

        <div
          v-if="mediaTypes.includes('videos')"
          id="videos"
          class="snap-center w-full h-[70svh] flex-[0_0_auto]">
          <div
            v-if="selectedTab === 'videos'"
            class="w-full h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
            @mousedown="disableIframe"
            @mouseup="enableIframe">
            <iframe
              v-for="(video, index) in videos"
              :id="`video-${index}`"
              :key="`video-${index}`"
              :src="videoSrc(video)"
              title="YouTube video player"
              class="w-full rounded-8"
              :class="iframeHeight(videos as string[])"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen />
          </div>
        </div>

        <div
          v-if="mediaTypes.includes('webinars')"
          id="webinars"
          class="snap-center w-full h-[70svh] aspect-[3/1]">
          <div
            v-if="selectedTab === 'webinars'"
            class="iframe-container w-full h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
            @mousedown="disableIframe"
            @mouseup="enableIframe">
            <iframe
              v-for="(webinar, index) in webinars"
              :id="`webinar-${index}`"
              :key="`webinar-${index}`"
              :src="videoSrc(webinar)"
              title="YouTube video player"
              class="w-full rounded-8"
              :class="iframeHeight(webinars as string[])"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen />
          </div>
        </div>

        <div
          v-if="mediaTypes.includes('tours')"
          id="tours"
          class="snap-center w-full h-[70svh] aspect-[3/1]">
          <div
            v-if="selectedTab === 'tours'"
            class="w-[90svw] max-w-[93rem] h-full grid auto-rows-max gap-16 px-8 overflow-y-auto scrollbar-hidden"
            @mousedown="disableIframe"
            @mouseup="enableIframe">
            <iframe
              v-for="(tour, index) in tours"
              :id="`tour-${index}`"
              :key="`tour-${index}`"
              :src="tour"
              title="3d tour"
              class="app-gallery w-full rounded-8"
              :class="iframeHeight(tours as string[])"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="flex flex-col justify-between items-center pt-10 lg:flex-row">
    <p v-sanitize="t('referralText', { bonusWithCurrency })" />

    <RoomieButton
      id="pd-gallery-refer-and-earn-button"
      data-lh-id="pd-gallery-refer-and-earn-button"
      type="button"
      class="w-full lg:w-fit"
      @click="handleReferAndEarnClick">
      <div
        class="w-full flex flex-row justify-center items-center gap-4">
        <p>
          {{ t('ctaText') }}
        </p>

        <ShareAndroidIcon class="w-20 h-20 fill-white pointer-events-none" />
      </div>
    </RoomieButton>
  </div>
</template>

<i18n src="./i18n.json" lang="json"></i18n>

<style lang="scss">
.pswp {
  &__button {
    &--arrow--next,
    &--arrow--prev {
      @apply w-50 h-50 mx-16;
      @apply bg-white rounded-full shadow-[0_0_10px_#0000004f];
      @apply grid place-content-center;
      @apply hover:bg-white;
    }

    &--arrow--next {
      & .pswp__icn {
        @apply fill-primary-600 top-10 -left-4 scale-[.6] rotate-180;
      }
    }

    &--arrow--prev {
      & .pswp__icn {
        @apply fill-primary-600 top-10 -left-0 scale-[.6];
      }
    }
  }

  &__icn-shadow {
    @apply hidden;
  }

  &__counter {
    @apply text-18 font-medium;
  }
}

.app-gallery {
  .app-slider {
    @apply -mx-24;

    height: 90%;

    &-items-holder {
      @apply h-full;
    }

    &__items-holder {
      @apply gap-0 h-full;
    }

    &__button {
      display: flex;
    }

    @media screen and (max-width: theme('screens.lg') ) {
      &__button {
      @apply w-24 h-24;

        & span {
          @apply w-24 h-24;
        }
      }
    }

    iframe {
      scroll-snap-align: center;
    }
  }

}

.gallery-elements-container {
  @apply overflow-y-hidden;

  scroll-snap-type: x mandatory;
  scroll-snap-stop: always;
}
</style>
